import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import type { ReactNode } from 'react';
import type { Progress, AIGeneratedTrophy } from '../types';
import * as storage from '../lib/storage';

interface ProgressContextType {
  progress: Progress;
  aiTrophies: AIGeneratedTrophy[];
  completeLesson: (lessonId: number, trophyId: string) => void;
  unlockTrophy: (trophyId: string) => void;
  updateGameHighScore: (score: number) => void;
  updateChildrenNames: (names: string[]) => void;
  resetProgress: () => void;
  refreshProgress: () => void;
  addAITrophy: (trophy: AIGeneratedTrophy) => Promise<void>;
  removeAITrophy: (id: string) => Promise<void>;
  refreshAITrophies: () => Promise<void>;
}

const ProgressContext = createContext<ProgressContextType | null>(null);

export const ProgressProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [progress, setProgress] = useState<Progress>(storage.getProgress());
  const [aiTrophies, setAITrophies] = useState<AIGeneratedTrophy[]>([]);

  const refreshProgress = useCallback(() => {
    setProgress(storage.getProgress());
  }, []);

  const refreshAITrophies = useCallback(async () => {
    const trophies = await storage.getAITrophies();
    setAITrophies(trophies);
  }, []);

  useEffect(() => {
    refreshProgress();
    refreshAITrophies();
  }, [refreshProgress, refreshAITrophies]);

  const completeLesson = useCallback((lessonId: number, trophyId: string) => {
    const updated = storage.completeLesson(lessonId, trophyId);
    setProgress(updated);
  }, []);

  const unlockTrophy = useCallback((trophyId: string) => {
    const updated = storage.unlockTrophy(trophyId);
    setProgress(updated);
  }, []);

  const updateGameHighScore = useCallback((score: number) => {
    const updated = storage.updateGameHighScore(score);
    setProgress(updated);
  }, []);

  const updateChildrenNames = useCallback((names: string[]) => {
    const updated = storage.updateChildrenNames(names);
    setProgress(updated);
  }, []);

  const resetProgressHandler = useCallback(() => {
    const fresh = storage.resetProgress();
    setProgress(fresh);
  }, []);

  const addAITrophy = useCallback(async (trophy: AIGeneratedTrophy) => {
    await storage.saveAITrophy(trophy);
    await refreshAITrophies();
  }, [refreshAITrophies]);

  const removeAITrophy = useCallback(async (id: string) => {
    await storage.deleteAITrophy(id);
    await refreshAITrophies();
  }, [refreshAITrophies]);

  const value: ProgressContextType = {
    progress,
    aiTrophies,
    completeLesson,
    unlockTrophy,
    updateGameHighScore,
    updateChildrenNames,
    resetProgress: resetProgressHandler,
    refreshProgress,
    addAITrophy,
    removeAITrophy,
    refreshAITrophies,
  };

  return (
    <ProgressContext.Provider value={value}>
      {children}
    </ProgressContext.Provider>
  );
};

export const useProgress = (): ProgressContextType => {
  const context = useContext(ProgressContext);
  if (!context) {
    throw new Error('useProgress must be used within a ProgressProvider');
  }
  return context;
};
